// Back-office component: shows the connected accounts of the *inspected* user (admin + AML detail),
// grouped by role context — those surfaces see every role context at once.
import { Skeleton, Stack, Typography } from "@mui/material";
import { ConnectedBankAccount } from "Components/Users/ConnectedBankAccounts/ConnectedBankAccount";
import { DeletedVerificationAccount } from "Components/Users/ConnectedBankAccounts/DeletedVerificationAccount";
import { RoleContextGroups } from "Components/Users/ConnectedBankAccounts/RoleContextGroups";
import { useGroupedBankingAccounts } from "Components/Users/ConnectedBankAccounts/useGroupedBankingAccounts";
import type {
  BankAccountListItem,
  BankVerificationSource,
} from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

type Props = {
  userID: number;
  verifiedIBAN?: string | null;
  bankVerificationSource?: BankVerificationSource | null;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  bankVerificationDate?: string | null;
  isReadOnly?: boolean;
};

export const ConnectedBankAccounts = (props: Props) => {
  const { userID, verifiedIBAN, bankVerificationSource, isReadOnly } = props;
  const { data, accounts, groups, hasLabeledGroups, isLoading, isError } =
    useGroupedBankingAccounts(userID);
  const { t } = useResource();

  if (isLoading) {
    return (
      <Stack gap={3}>
        <Skeleton height={40} variant="rounded" />
        <Skeleton height={40} variant="rounded" />
        <Skeleton height={40} variant="rounded" />
        <Skeleton height={40} variant="rounded" />
      </Stack>
    );
  }

  if (!data || !accounts || isError) {
    return (
      <Typography>
        {t(Resources.Pages.Content.Users.ConnectedBankAccounts.LoadingError)}
      </Typography>
    );
  }

  const isUserVerified = !!verifiedIBAN && !!bankVerificationSource;
  if (accounts.length === 0 && !isUserVerified) {
    return (
      <Typography>
        {t(Resources.Pages.Content.Users.ConnectedBankAccounts.NoBankAccounts)}
      </Typography>
    );
  }

  const isVerificationAccountDeleted = !accounts.some(
    ({ iban }) => iban === verifiedIBAN,
  );

  const renderAccount = (account: BankAccountListItem) => (
    <ConnectedBankAccount
      key={account.bankAccountID}
      bankAccount={account}
      userID={userID}
      verifiedIBAN={verifiedIBAN}
      bankVerificationSource={bankVerificationSource}
      isReadOnly={isReadOnly}
    />
  );

  return (
    <Stack>
      {hasLabeledGroups ? (
        <RoleContextGroups groups={groups} renderAccount={renderAccount} />
      ) : (
        accounts.map(renderAccount)
      )}

      {isVerificationAccountDeleted && isUserVerified && (
        <DeletedVerificationAccount {...props} verifiedIBAN={verifiedIBAN} />
      )}
    </Stack>
  );
};
